export const INDICATORS_BITMAP_PREFIX = 'b:';

/**
 * IMPORTANT:
 * - Never change existing IDs.
 * - Only append new entries with new IDs when adding indicators.
 * - Reordering object properties is fine; IDs are the source of truth.
 */
export const COMPARATOR_INDICATOR_STABLE_IDS: Record<string, number> = {
  // Dados básicos
  descricao: 0,
  administradora: 1,
  classificacao: 2,
  subclassificacao: 3,
  segmento: 4,
  tipo_fundo: 5,

  // Fundamentos
  preco_atual: 10,
  ultimo_dividendo: 11,
  vp_por_cota: 12,
  patrimonio: 13,
  pvp: 14,
  valor_mercado: 15,
  dy12m: 16,
  dividendos12m: 17,
  numero_cotistas: 18,
  numero_cotas: 19,

  // Mercado
  liquidez_30d: 20,
  dy_mes: 21,
  dy_growth_12m: 22,
  vp_growth_12m: 23,
  pvp_discount_hist: 24,

  // Portfólio (numéricos)
  cash_resources_value: 30,
  receivables_value: 31,
  payables_value: 32,
  net_liquidity_value: 33,
  cash_over_invested_pct: 34,
  portfolio_financial_assets_pct: 35,
  portfolio_funds_pct: 36,
  real_estate_total_assets: 37,
  real_estate_vacancy_avg_pct: 38,
  real_estate_delinquency_avg_pct: 39,
  real_estate_typical_contracts_pct: 40,
  real_estate_atypical_contracts_pct: 41,
  paper_top5_concentration_pct: 42,
  paper_inflation_exposure_pct: 43,
  paper_maturity_upto_12m_pct: 44,
  paper_dominant_indexer_pct: 45,

  // Quantitativos
  vacancia: 50,
  inadimplencia: 51,
  selic_sensitivity: 52,
  leverage_index: 53,
  vol_21d: 54,
  distance_12m_high: 55,
  conc_tijolo: 56,
  conc_papel: 57,

  // Cotação (gráficos)
  grafico_cotacao_normalizada: 60,
  grafico_cotacao_dividendos: 61,
  grafico_dividendos_periodo: 62,

  // Risco e Momento
  retorno_periodo: 70,
  max_drawdown: 71,
  vol_realizada: 72,

  // Portfólio (visuais)
  portfolio_top5: 80,
  portfolio_mix: 81,
  portfolio_concentracao: 82,
};

function parseBase36BigInt(value: string): bigint | null {
  const source = value.trim().toLowerCase();
  if (!source) return null;
  const alphabet = '0123456789abcdefghijklmnopqrstuvwxyz';
  let result = BigInt(0);
  for (const char of source) {
    const digit = alphabet.indexOf(char);
    if (digit < 0) return null;
    result = result * BigInt(36) + BigInt(digit);
  }
  return result;
}

export function encodeComparatorIndicatorsBitmap(keys: string[]): string {
  let mask = BigInt(0);
  for (const key of keys) {
    const id = COMPARATOR_INDICATOR_STABLE_IDS[key];
    if (id === undefined) continue;
    mask |= BigInt(1) << BigInt(id);
  }
  return `${INDICATORS_BITMAP_PREFIX}${mask.toString(36)}`;
}

export function decodeComparatorIndicatorsBitmap(bitmapValue: string): string[] {
  const mask = parseBase36BigInt(bitmapValue);
  if (mask === null) return [];

  const selected: string[] = [];
  const entries = Object.entries(COMPARATOR_INDICATOR_STABLE_IDS).sort((a, b) => a[1] - b[1]);
  for (let index = 0; index < entries.length; index += 1) {
    const [key, id] = entries[index];
    const isSet = (mask & (BigInt(1) << BigInt(id))) !== BigInt(0);
    if (isSet) selected.push(key);
  }
  return selected;
}

export function assertComparatorIndicatorIdsCoverage(keys: string[]) {
  const ids = Object.values(COMPARATOR_INDICATOR_STABLE_IDS);
  const duplicates = ids.filter((id, idx) => ids.indexOf(id) !== idx);
  if (duplicates.length > 0) {
    throw new Error(`Duplicate comparator indicator IDs found: ${duplicates.join(', ')}`);
  }

  const missing = keys.filter((key) => COMPARATOR_INDICATOR_STABLE_IDS[key] === undefined);
  if (missing.length > 0) {
    throw new Error(`Missing stable IDs for comparator indicators: ${missing.join(', ')}`);
  }
}
